import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Chip,
    CircularProgress,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import AutoFixHighOutlinedIcon from "@mui/icons-material/AutoFixHighOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import _ from "lodash";
import moment from "moment";
import { useSnackbar } from "notistack";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import CustomPagination from "app/widgets/Shared/Pagination/CustomPagination";
import { Search, SearchIconWrapper, StyledInputBase } from "app/shared/SearchGlobal/style";
import { downloadBuffer } from "app/utils/appHelpers";
import erpInventoryServices from "app/services/erp-inventory-services";
import centreServices from "app/services/centre-services";
import InventorySettingsDialog from "./InventorySettingsDialog";
import SetPositionDialog from "./SetPositionDialog";
import SlottingDialog from "./SlottingDialog";
import PrintLabelsDialog from "./PrintLabelsDialog";
import ProductDetailDrawer from "./ProductDetailDrawer";
import InventoryImportModal from "./InventoryImportModal";
import ExcelExportButton from "app/widgets/Shared/ExcelExportButton";

const PAGE_SIZE = 25;

// La bodega elegida se recuerda por navegador. Es preferencia personal: el
// bodeguero de Macul entra siempre a Macul sin que nadie le configure nada.
const CENTRE_KEY = "haulers.inventory.centre";

const COLUMNS_GROUPED = [
    { key: "code", label: "Código", sortable: true },
    { key: "name", label: "Producto", sortable: true },
    { key: "stock", label: "Stock total", sortable: true, align: "right" },
    { key: "warehouses", label: "Por bodega", sortable: false },
    { key: "route", label: "Posición", sortable: true },
    { key: "picks", label: "Rotación", sortable: true, align: "right", help: "Veces que se fue a buscar en la ventana. Es lo que manda en dónde conviene tenerlo." },
    { key: "cover", label: "Cobertura", sortable: true, align: "right", help: "Días que dura el stock al ritmo de salida actual." },
];

const COLUMNS_DETAIL = [
    { key: "code", label: "Código", sortable: true },
    { key: "name", label: "Producto", sortable: true },
    { key: "centre", label: "Bodega", sortable: false },
    { key: "stock", label: "Stock", sortable: true, align: "right" },
    { key: "location", label: "Ubicación en el ERP", sortable: true },
    { key: "route", label: "Posición en Haulers", sortable: true },
];

const COVER_STYLE = {
    critico: { color: "error", label: "Crítico" },
    alerta: { color: "warning", label: "Alerta" },
    ok: { color: "success", label: "OK" },
    sin_demanda: { color: "default", label: "Sin demanda" },
};

const InventoryList = () => {
    const { enqueueSnackbar } = useSnackbar();
    const { authUser } = useJumboAuth();

    const [rows, setRows] = useState([]);
    const [meta, setMeta] = useState({});
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [positionProduct, setPositionProduct] = useState(null);
    const [slottingOpen, setSlottingOpen] = useState(false);
    const [labelsOpen, setLabelsOpen] = useState(false);
    const [importOpen, setImportOpen] = useState(false);
    const [detailCode, setDetailCode] = useState(null);
    // Seleccion para imprimir etiquetas. Se guarda el objeto y no solo el codigo
    // para que el dialogo muestre nombre y EAN sin volver a consultar.
    const [selected, setSelected] = useState([]);
    // Contador para forzar recarga tras guardar. setPage(p => p) no sirve:
    // React descarta un estado identico y el efecto no vuelve a correr.
    const [reloadToken, setReloadToken] = useState(0);
    const [page, setPage] = useState(1);

    const [grouped, setGrouped] = useState(true);
    const [sort, setSort] = useState("code");
    const [direction, setDirection] = useState("asc");

    const [searchInput, setSearchInput] = useState("");
    const [query, setQuery] = useState("");
    const [centres, setCentres] = useState([]);
    const [centreId, setCentreId] = useState(() => localStorage.getItem(CENTRE_KEY) || "");
    const [locationPresence, setLocationPresence] = useState("all");
    const [binPresence, setBinPresence] = useState("all");
    const [stockPresence, setStockPresence] = useState("all");
    const [barcodePresence, setBarcodePresence] = useState("all");
    const [abcClass, setAbcClass] = useState("all");
    const [movement, setMovement] = useState("all");

    useEffect(() => {
        const load = async () => {
            try {
                const data = await centreServices.searchByCompany();
                setCentres(Array.isArray(data) ? data : data?.data || []);
            } catch (_error) {
                setCentres([]);
            }
        };
        load();
    }, []);

    useEffect(() => {
        if (centreId) localStorage.setItem(CENTRE_KEY, String(centreId));
        else localStorage.removeItem(CENTRE_KEY);
    }, [centreId]);

    const buildParams = useCallback((withPagination = true) => {
        const params = new URLSearchParams();
        if (withPagination) {
            params.set("page", String(page));
            params.set("per_page", String(PAGE_SIZE));
        }
        params.set("group", grouped ? "product" : "detail");
        params.set("sort", sort);
        params.set("direction", direction);
        if (query) params.set("q", query);
        if (centreId) params.set("centre_id", centreId);
        if (locationPresence !== "all") params.set("location_presence", locationPresence);
        if (binPresence !== "all") params.set("bin_presence", binPresence);
        if (stockPresence !== "all") params.set("stock_presence", stockPresence);
        if (barcodePresence !== "all") params.set("barcode_presence", barcodePresence);
        if (abcClass !== "all") params.set("abc_class", abcClass);
        if (movement !== "all") params.set("movement", movement);
        return `?${params.toString()}`;
    }, [abcClass, barcodePresence, binPresence, centreId, direction, grouped, locationPresence, movement, page, query, sort, stockPresence]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);

        erpInventoryServices.getInventories(buildParams())
            .then((data) => {
                if (cancelled) return;
                setRows(data?.data || []);
                setMeta(data?.meta || {});
            })
            .catch(() => {
                if (!cancelled) setRows([]);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => { cancelled = true; };
    }, [buildParams, reloadToken]);

    const debouncedSearch = useMemo(
        () => _.debounce((value) => {
            setPage(1);
            setQuery(value);
        }, 450),
        []
    );

    useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

    const summary = meta.summary || {};
    // Una sola fuente de posiciones por compania, definida en los ajustes.
    const managedByHaulers = meta.positions_managed_by === "haulers";
    const columns = grouped ? COLUMNS_GROUPED : COLUMNS_DETAIL;

    const applyFilter = (setter) => (event) => {
        setPage(1);
        setter(event.target.value);
    };

    const toggleSelect = (row) => {
        setSelected((current) =>
            current.some((item) => item.code === row.code)
                ? current.filter((item) => item.code !== row.code)
                : [...current, { code: row.code, name: row.name, barcode: row.barcode }]
        );
    };

    const allSelected = rows.length > 0 && rows.every((row) => selected.some((s) => s.code === row.code));

    const toggleSelectAll = () => {
        if (allSelected) {
            setSelected((current) => current.filter((item) => !rows.some((row) => row.code === item.code)));
        } else {
            const nuevos = rows
                .filter((row) => !selected.some((s) => s.code === row.code))
                .map((row) => ({ code: row.code, name: row.name, barcode: row.barcode }));
            setSelected((current) => [...current, ...nuevos]);
        }
    };

    const toggleSort = (key) => {
        if (sort === key) {
            setDirection(direction === "asc" ? "desc" : "asc");
        } else {
            setSort(key);
            // Cobertura y rotación se miran de menor a mayor y de mayor a menor
            // respectivamente: lo urgente y lo más movido van arriba sin que
            // haya que hacer un segundo clic.
            setDirection(key === "cover" ? "asc" : key === "picks" ? "desc" : "asc");
        }
        setPage(1);
    };

    const exportRows = async () => {
        try {
            setExporting(true);
            const data = await erpInventoryServices.exportInventories(buildParams(false));
            downloadBuffer(data, `inventario_${moment().format("YYYYMMDDHHmmss")}.xlsx`);
        } catch (_error) {
            enqueueSnackbar("No se pudo exportar", { variant: "error" });
        } finally {
            setExporting(false);
        }
    };

    // La unidad va pegada al numero y no en columna propia: ocupa una fraccion
    // del ancho y se lee igual. Hoy Relbase manda unit_item vacio, asi que sale
    // de las lineas de pedido — para Grupo DK es UN en todo.
    const renderStock = (value, unit) => (
        <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="baseline">
            <Typography variant="body2">{value ?? "-"}</Typography>
            {unit && (
                <Typography variant="caption" color="text.secondary">{unit}</Typography>
            )}
        </Stack>
    );

    const renderCover = (row) => {
        const level = row.cover_level;
        if (level === "sin_demanda") {
            return (
                <Tooltip title="No tuvo salidas en la ventana. No está en riesgo de quiebre: está frenado.">
                    <Typography variant="body2" color="text.secondary">—</Typography>
                </Tooltip>
            );
        }
        if (row.days_of_cover === null || row.days_of_cover === undefined) {
            return <Typography variant="body2" color="text.secondary">—</Typography>;
        }

        const style = COVER_STYLE[level] || COVER_STYLE.ok;
        return (
            <Tooltip title={`${row.demand?.daily_units?.toFixed?.(2) ?? 0} unidades por día`}>
                <Chip size="small" color={style.color} variant={level === "ok" ? "outlined" : "filled"}
                      label={`${row.days_of_cover} d`} />
            </Tooltip>
        );
    };

    return (
        <Box>
            <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between"
                   alignItems={{ xs: "stretch", md: "flex-end" }} spacing={2} sx={{ mb: 2 }}>
                <Box>
                    <Typography variant="h1" sx={{ mb: 0.5 }}>Inventario</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Cada producto del ERP con su stock, su posición y su rotación.
                    </Typography>
                </Box>

                <Stack direction="row" spacing={1} alignItems="center">
                    {meta.synced_at && (
                        <Tooltip title="Es una foto de la última sincronización, no el saldo al segundo. El stock exacto se consulta al preparar.">
                            <Typography variant="caption" color="text.secondary">
                                Sincronizado {moment(meta.synced_at).fromNow()}
                            </Typography>
                        </Tooltip>
                    )}
                    <ExcelExportButton size="small" loading={exporting} onClick={exportRows} />
                    <Button size="small" variant="outlined" startIcon={<UploadFileOutlinedIcon />}
                            onClick={() => setImportOpen(true)}>
                        Importar
                    </Button>
                    {selected.length > 0 && (
                        <Button size="small" variant="contained" color="secondary"
                                startIcon={<PrintOutlinedIcon />} onClick={() => setLabelsOpen(true)}>
                            Etiquetas ({selected.length})
                        </Button>
                    )}
                    {managedByHaulers && (
                        <Button size="small" variant="contained" startIcon={<AutoFixHighOutlinedIcon />}
                                onClick={() => setSlottingOpen(true)}>
                            Sugerir posiciones
                        </Button>
                    )}
                    <Button size="small" variant="outlined" startIcon={<SettingsOutlinedIcon />}
                            onClick={() => setSettingsOpen(true)}>
                        Ajustes
                    </Button>
                </Stack>
            </Stack>

            {/* Los contadores son de lo filtrado, no del universo: antes decian
                el total de la compañía mientras la paginación mostraba otra cosa. */}
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                <Chip label={`${summary.products ?? 0} productos`} />
                <Tooltip title={managedByHaulers
                    ? "Las posiciones se administran en Haulers. Cámbialo en Ajustes."
                    : "Las posiciones las dicta el ERP. Cámbialo en Ajustes."}>
                    <Chip size="small" variant="outlined"
                          label={managedByHaulers ? "Posiciones: Haulers" : "Posiciones: ERP"} />
                </Tooltip>
                <Chip label={`${summary.rows ?? 0} filas`} variant="outlined" />
                <Chip label={`${summary.positioned ?? 0} con posición`} variant="outlined" color="success" />
                {summary.needs_position > 0 && <Chip label={`${summary.needs_position} sin posición`} variant="outlined" color="warning" />}
                {summary.unparsed > 0 && (
                    <Tooltip title="El ERP reporta una ubicación que no identifica un rack (Azotea, Tienda). Conviene arreglarlo en el ERP.">
                        <Chip label={`${summary.unparsed} texto sin ubicar`} variant="outlined" color="error" />
                    </Tooltip>
                )}
                {summary.without_barcode > 0 && <Chip label={`${summary.without_barcode} sin EAN`} variant="outlined" color="error" />}
            </Stack>

            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Stack spacing={2}>
                        <Stack direction={{ xs: "column", lg: "row" }} spacing={2}>
                            <Search sx={{ flexGrow: 1 }}>
                                <SearchIconWrapper><SearchIcon /></SearchIconWrapper>
                                <StyledInputBase
                                    placeholder="Código, nombre, código de barra o ubicación"
                                    value={searchInput}
                                    onChange={(event) => {
                                        setSearchInput(event.target.value);
                                        debouncedSearch(event.target.value);
                                    }}
                                />
                            </Search>

                            <ToggleButtonGroup size="small" exclusive value={grouped}
                                               onChange={(_e, value) => { if (value !== null) { setGrouped(value); setPage(1); } }}>
                                <ToggleButton value>Por producto</ToggleButton>
                                <ToggleButton value={false}>Por bodega</ToggleButton>
                            </ToggleButtonGroup>
                        </Stack>

                        <Stack direction={{ xs: "column", lg: "row" }} spacing={2} flexWrap="wrap" useFlexGap>
                            {centres.length > 1 && (
                                <TextField select size="small" label="Bodega" value={centreId}
                                           onChange={applyFilter(setCentreId)} sx={{ minWidth: 160 }}>
                                    <MenuItem value="">Todas</MenuItem>
                                    {centres.map((centre) => (
                                        <MenuItem key={centre.id} value={centre.id}>{centre.name}</MenuItem>
                                    ))}
                                </TextField>
                            )}

                            <TextField select size="small" label="Posición" value={binPresence}
                                       onChange={applyFilter(setBinPresence)} sx={{ minWidth: 170 }}>
                                <MenuItem value="all">Todas</MenuItem>
                                <MenuItem value="positioned">Con posición</MenuItem>
                                <MenuItem value="unpositioned">Sin posición</MenuItem>
                            </TextField>

                            <TextField select size="small" label="Ubicación" value={locationPresence}
                                       onChange={applyFilter(setLocationPresence)} sx={{ minWidth: 150 }}>
                                <MenuItem value="all">Todas</MenuItem>
                                <MenuItem value="with_location">Con ubicación</MenuItem>
                                <MenuItem value="without_location">Sin ubicación</MenuItem>
                            </TextField>

                            <TextField select size="small" label="Código de barra" value={barcodePresence}
                                       onChange={applyFilter(setBarcodePresence)} sx={{ minWidth: 150 }}>
                                <MenuItem value="all">Todos</MenuItem>
                                <MenuItem value="without_barcode">Sin EAN</MenuItem>
                                <MenuItem value="with_barcode">Con EAN</MenuItem>
                            </TextField>

                            <TextField select size="small" label="Stock" value={stockPresence}
                                       onChange={applyFilter(setStockPresence)} sx={{ minWidth: 130 }}>
                                <MenuItem value="all">Todo</MenuItem>
                                <MenuItem value="with_stock">Con stock</MenuItem>
                                <MenuItem value="without_stock">Sin stock</MenuItem>
                            </TextField>

                            <TextField select size="small" label="Clase ABC" value={abcClass}
                                       onChange={applyFilter(setAbcClass)} sx={{ minWidth: 130 }}>
                                <MenuItem value="all">Todas</MenuItem>
                                <MenuItem value="A">A (más movido)</MenuItem>
                                <MenuItem value="B">B</MenuItem>
                                <MenuItem value="C">C</MenuItem>
                            </TextField>

                            <TextField select size="small" label="Movimiento" value={movement}
                                       onChange={applyFilter(setMovement)} sx={{ minWidth: 190 }}>
                                <MenuItem value="all">Todos</MenuItem>
                                <MenuItem value="moving">Con movimiento</MenuItem>
                                <MenuItem value="stale">
                                    Frenados +{meta.thresholds?.stale_days ?? 90}d
                                </MenuItem>
                            </TextField>
                        </Stack>
                    </Stack>
                </CardContent>
            </Card>

            {loading && (
                <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
            )}

            {!loading && (
                <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                {grouped && (
                                    <TableCell padding="checkbox">
                                        <Checkbox size="small" checked={allSelected}
                                                  indeterminate={!allSelected && rows.some((row) => selected.some((s) => s.code === row.code))}
                                                  onChange={toggleSelectAll} />
                                    </TableCell>
                                )}
                                {columns.map((column) => (
                                    <TableCell key={column.key} align={column.align || "left"}>
                                        {column.sortable ? (
                                            <TableSortLabel
                                                active={sort === column.key}
                                                direction={sort === column.key ? direction : "asc"}
                                                onClick={() => toggleSort(column.key)}
                                            >
                                                {column.help ? (
                                                    <Tooltip title={column.help}><span>{column.label}</span></Tooltip>
                                                ) : column.label}
                                            </TableSortLabel>
                                        ) : column.label}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {grouped && rows.map((row) => (
                                <TableRow key={row.code} hover selected={selected.some((s) => s.code === row.code)}>
                                    <TableCell padding="checkbox">
                                        <Checkbox size="small"
                                                  checked={selected.some((s) => s.code === row.code)}
                                                  onChange={() => toggleSelect(row)} />
                                    </TableCell>
                                    <TableCell>{row.code}</TableCell>
                                    <TableCell>
                                        <Box onClick={() => setDetailCode(row.code)}
                                             sx={{ cursor: "pointer", "&:hover .nombre": { textDecoration: "underline" } }}>
                                            <Typography variant="body2" className="nombre" color="primary">
                                                {row.name || "-"}
                                            </Typography>
                                            {!row.barcode && (
                                                <Typography variant="caption" color="error.main">sin EAN</Typography>
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell align="right">{renderStock(row.total_stock, row.unit)}</TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                            {row.warehouses?.map((w) => (
                                                <Tooltip key={`${row.code}-${w.centre_id}`} title={w.location || "sin ubicación"}>
                                                    <Chip size="small" variant="outlined"
                                                          label={`${w.centre_name || "?"}: ${w.stock}`} />
                                                </Tooltip>
                                            ))}
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <Box onClick={() => managedByHaulers && setPositionProduct(row)}
                                             sx={{ cursor: managedByHaulers ? "pointer" : "default" }}>
                                            {row.bin?.structured ? (
                                                <Chip size="small" variant="outlined" label={row.bin.code} color="success" />
                                            ) : managedByHaulers ? (
                                                <Tooltip title="Sin posición. Haz clic para asignarla.">
                                                    <Chip size="small" variant="outlined" label="Asignar" />
                                                </Tooltip>
                                            ) : row.bin ? (
                                                <Tooltip title="El ERP reporta una ubicación que no identifica un rack. Se corrige en el ERP.">
                                                    <Chip size="small" color="warning" variant="outlined" label="Sin ubicar" />
                                                </Tooltip>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">-</Typography>
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell align="right">
                                        {row.demand?.picks > 0 ? (
                                            <Tooltip title={`${row.demand.units} unidades · último movimiento hace ${row.demand.days_since_movement ?? "?"} días`}>
                                                <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                                                    <Typography variant="body2">{row.demand.picks}</Typography>
                                                    {row.demand.abc_class && (
                                                        <Chip size="small" label={row.demand.abc_class}
                                                              color={row.demand.abc_class === "A" ? "primary" : "default"}
                                                              variant="outlined" />
                                                    )}
                                                </Stack>
                                            </Tooltip>
                                        ) : <Typography variant="body2" color="text.secondary">—</Typography>}
                                    </TableCell>
                                    <TableCell align="right">{renderCover(row)}</TableCell>
                                </TableRow>
                            ))}

                            {!grouped && rows.map((row) => (
                                <TableRow key={row.id} hover>
                                    <TableCell>{row.code}</TableCell>
                                    <TableCell>
                                        <Typography variant="body2" color="primary" sx={{ cursor: "pointer" }}
                                                    onClick={() => setDetailCode(row.code)}>
                                            {row.name || "-"}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>{row.centre_name || "-"}</TableCell>
                                    <TableCell align="right">{renderStock(row.stock, row.unit)}</TableCell>
                                    <TableCell>{row.location || "-"}</TableCell>
                                    <TableCell>
                                        {row.bin ? (
                                            <Chip size="small" color={row.bin.structured ? "success" : "warning"}
                                                  variant="outlined" label={row.bin.structured ? row.bin.code : "Por mapear"} />
                                        ) : <Typography variant="body2" color="text.secondary">-</Typography>}
                                    </TableCell>
                                </TableRow>
                            ))}

                            {rows.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={columns.length + (grouped ? 1 : 0)}>
                                        <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
                                            No hay resultados con estos filtros.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <CustomPagination
                currentPage={meta.current_page || 1}
                totalPages={meta.total_pages || 1}
                totalCount={meta.total_count || 0}
                pageSize={PAGE_SIZE}
                onPageSelect={(nextPage) => setPage(nextPage)}
            />

            <ProductDetailDrawer
                open={Boolean(detailCode)}
                code={detailCode}
                onClose={() => setDetailCode(null)}
            />

            <InventoryImportModal
                open={importOpen}
                centres={centres}
                defaultCentreId={centreId}
                onClose={() => setImportOpen(false)}
                onImported={() => setReloadToken((token) => token + 1)}
            />

            <PrintLabelsDialog
                open={labelsOpen}
                products={selected}
                onClose={() => setLabelsOpen(false)}
                onPrinted={() => setSelected([])}
            />

            <SlottingDialog
                open={slottingOpen}
                centres={centres}
                onClose={() => setSlottingOpen(false)}
                onApplied={() => setReloadToken((token) => token + 1)}
            />

            <SetPositionDialog
                open={Boolean(positionProduct)}
                product={positionProduct}
                centres={centres}
                onClose={() => setPositionProduct(null)}
                onSaved={() => setReloadToken((token) => token + 1)}
            />

            <InventorySettingsDialog
                open={settingsOpen}
                onClose={() => setSettingsOpen(false)}
                companyId={authUser?.current_company?.id}
                thresholds={meta.thresholds}
                managedByHaulers={managedByHaulers}
                onSaved={() => setReloadToken((token) => token + 1)}
            />
        </Box>
    );
};

export default InventoryList;
